import styles from "./Grading.module.scss";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { EnquiryResponse, GradingAnswer, QuestionInfo } from "./types";
import { questionInformation } from "./questionInformation";
import Question from "./Question/Question";
import { dispatchAnalyticsEvent } from "../../../../../utils";
import {
  createEnquiry,
  CreateEnquiryResponse,
} from "../../../client/createEnquiry";
import { useTradeInContext } from "../../../TradeInContext";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { TRADE_IN_STEPS } from "../TradeInModal";
import NextStep, { TradeInSteps } from "../NextStep/NextStep";
import { TRADE_IN_UNAVAILABLE_WARNING } from "../DeviceSearch/types";
import { TradeInEvents } from "../../../TradeIn";
import { removeEmpty } from "@/utils/array/removeEmpty";

const Grading = () => {
  const {
    tradeInBaseUrl,
    selectedDevice,
    setEnquiryOptions,
    setGradingAnswers,
    setCurrentScreen,
    setPaymentOptions,
    onApiError,
  } = useTradeInContext();

  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [lastAnswered, setLastAnswered] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const gradingListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastAnswered !== undefined) {
      const questions = gradingListRef.current?.children;
      setTimeout(() => {
        questions &&
          questions[questions.length - 1]?.scrollIntoView({
            behavior: "smooth",
          });
      }, 100);
    }
  }, [lastAnswered]);

  if (
    !selectedDevice ||
    !selectedDevice.grading ||
    selectedDevice.grading.length === 0
  )
    return null;

  const { grading = [] } = selectedDevice;
  const handleAnswer =
    (questionIndex: number) =>
    (questionId: string, answer: boolean): void => {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        [questionId]: answer,
      }));

      const question = grading.find((gradingQ) => gradingQ.id === questionId);
      dispatchTradeInGradingAnswer(
        question?.question || "",
        answer ? "yes" : "no",
      );

      setLastAnswered((lastAnsweredIndex) => {
        if (lastAnsweredIndex === undefined) return questionIndex;

        return questionIndex > lastAnsweredIndex
          ? questionIndex
          : lastAnsweredIndex;
      });
    };
  const shouldRenderQuestion = (
    questionIndex: number,
    lastAnswered: number | undefined,
  ): boolean => {
    if (lastAnswered === undefined) return questionIndex === 0;
    return questionIndex <= lastAnswered + 1;
  };

  const areQuestionsComplete = (): boolean =>
    lastAnswered === grading.length - 1;

  const getQuestionInformation = (
    questionId: string,
  ): QuestionInfo | undefined => {
    return questionInformation.questions.find(
      (help) => help.questionID === questionId,
    );
  };

  const dispatchTradeInGradingAnswer = (question: string, answer: string) => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "global.interaction",
        type: "pdp",
        subType: `trade in - answer - ${answer}`,
        additionalInfo: { question },
        properties: {},
        products: [],
      },
      {},
    );
  };

  const dispatchTradeInSubmitGrading = () => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "global.interaction",
        type: "pdp",
        subType: "trade in - device info - submit",
        additionalInfo: {},
        properties: {},
        products: [],
      },
      {},
    );
  };
  const onEnquiryComplete = (
    gradingResponses: GradingAnswer[],
    enquiryResponse: EnquiryResponse,
  ) => {
    setEnquiryOptions(enquiryResponse);
    setPaymentOptions(enquiryResponse.paymentOptions);
    setGradingAnswers(gradingResponses);
    setCurrentScreen(TRADE_IN_STEPS.PAYMENT_OPTIONS);
    dispatchMonetateQEvent(
      TradeInEvents.PW_TRADE_IN_INFO_ABOUT_YOUR_DEVICE_STEP_CTA,
    );
  };

  const onClick = async (e: MouseEvent) => {
    e.preventDefault();
    const selectedAnswers = removeEmpty(
      grading.map((grade) => {
        const answer = answers[grade.id];
        return answer !== undefined
          ? {
              id: grade.id,
              response: answer,
            }
          : null;
      }),
    );
    dispatchTradeInSubmitGrading();
    const requestBody = {
      deviceId: selectedDevice?.deviceId,
      grading: [...selectedAnswers],
    };
    setIsLoading(true);

    const abort = new AbortController();

    const timeoutId = setTimeout(() => {
      abort.abort();
    }, 13000);

    const response: CreateEnquiryResponse = await createEnquiry(
      tradeInBaseUrl,
      requestBody,
      abort,
    );

    clearTimeout(timeoutId);

    if (!response || response.status < 200 || response.status > 299) {
      onApiError(TRADE_IN_UNAVAILABLE_WARNING);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    const enquiryResponse = response.enquiry as EnquiryResponse;
    onEnquiryComplete(selectedAnswers, enquiryResponse);
  };

  return (
    <div className={styles.grading}>
      <h3 className={styles.grading__title}>Information about your device</h3>
      <div className={styles.grading__answerList} ref={gradingListRef}>
        {grading.map(
          (question, index) =>
            shouldRenderQuestion(index, lastAnswered) && (
              <div key={question.id}>
                <Question
                  {...question}
                  step={`${index + 1}/${grading.length}`}
                  key={question.id}
                  onAnswered={handleAnswer(index)}
                  information={getQuestionInformation(question.id)}
                />
              </div>
            ),
        )}
      </div>
      <div className={styles.grading__footer}>
        {areQuestionsComplete() && (
          <div className={styles.grading__nextStep}>
            <div className={styles.grading__continueBtnWrapper}>
              <PrimaryButton
                className={styles.grading__continueBtn}
                tag="button"
                type="button"
                data-cy="continue-btn"
                disabled={!areQuestionsComplete()}
                onClick={onClick}
                submitting={isLoading}
              >
                Continue
              </PrimaryButton>
            </div>
            <NextStep step={TradeInSteps.PAYOUT} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Grading;
